import { PlayerImage } from "@/components/PlayerImage";
import { AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { roundToFixed } from "@/utils";
import { CheckIcon } from "@roman/shared/components/Icons";
import type { FantasyPointDetails, PlayerResult } from "@roman/shared/types/Result";
import { cn, getColorStyleByRank } from "@roman/shared/utils";
import { ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface PlayerRowProps {
  player: PlayerResult;
  rank: number;
  isInMyTeam: boolean;
  isCaptain: boolean;
  isViceCaptain: boolean;
  setExpandedValues: React.Dispatch<React.SetStateAction<string[]>>;
  roundEntryUnitId: string;
  entryId: string;
}
export const PLAYER_STATS_DISPLAY_MAP: Record<string, string> = {
  goalsScored: "ゴール",
  assists: "アシスト",
  shot: "シュート",
  shotOnGoal: "枠内シュート",
  crosses: "クロス",
  assistedShot: "決定機パス",
  accuratePass: "正確なパス",
  foulDrawn: "被ファウル",
  foulConceded: "ファウル",
  tackleWon: "タックル成功",
  passesIntercepted: "インターセプト",
  yellowCard: "イエローカード",
  redCard: "レッドカード",
  cleanSheet: "クリーンシート",

  // ゴールキーパー専用統計
  saves: "セーブ",
  goalConceded: "失点",
  goalkeeperCleanSheet: "クリーンシート",
  win: "勝利",
  penaltyKickSave: "PKセーブ",
};
export const PlayerRow = ({
  player,
  rank,
  isInMyTeam,
  isCaptain,
  isViceCaptain,
  setExpandedValues,
  roundEntryUnitId,
  entryId,
}: PlayerRowProps) => {
  const navigate = useNavigate();
  const fantasyPointDetailsKeys = Object.keys(player.fantasyPointDetails) as (keyof FantasyPointDetails)[];

  const toggleExpandedValue = () => {
    if (fantasyPointDetailsKeys.length) {
      setExpandedValues((prev: string[]) => {
        const index = prev.indexOf(`item-${player.id}`);
        if (index === -1) {
          return [...prev, `item-${player.id}`];
        }
        return prev.filter((value: string) => value !== `item-${player.id}`);
      });
    }
  };

  return (
    <AccordionItem
      value={player.id}
      className="w-full text-icon px-1 data-[state=open]:bg-[#F3F8FF] first:border-t first:border-light-gray"
    >
      <AccordionTrigger
        className="flex items-center space-x-2 hover:no-underline w-full pointer-events-none h-[72px]"
        iconClassName="w-0 hidden"
      >
        <div className="flex items-center gap-2">
          <p className={cn("w-6 text-center text-2xl", getColorStyleByRank(rank))}>{rank}</p>

          <PlayerImage
            url={player.imageUrl}
            name={player.name}
            isCaptain={isCaptain}
            isViceCaptain={isViceCaptain}
            badgeClassName="w-[20px] h-[10px]"
          />
        </div>
        <div
          className={cn("flex flex-col gap-1 w-full", fantasyPointDetailsKeys.length && "pointer-events-auto")}
          onClick={toggleExpandedValue}
        >
          <div className="flex items-center gap-1">
            {isInMyTeam && (
              <div
                id="player-my-team"
                className="rounded-full p-1 w-[18px] h-[18px] flex items-center justify-center cursor-pointer bg-secondary"
              >
                <CheckIcon className="text-white" />
              </div>
            )}
            <p className="text-sm font-semibold">{player.name}</p>
          </div>

          <div
            className="font-medium flex items-center text-primary cursor-pointer pointer-events-auto w-fit"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/results/${roundEntryUnitId}/entries/${entryId}?playerId=${player.id}`);
            }}
          >
            <span className="text-xs font-medium">選択履歴</span>
            <ChevronRightIcon strokeWidth={2} className="min-w-[20px]" />
          </div>
        </div>

        <span
          className={cn("font-semibold text-base text-black", fantasyPointDetailsKeys.length && "pointer-events-auto")}
          onClick={toggleExpandedValue}
        >
          {player.fantasyPoint.toFixed(2)}pt
        </span>

        <ChevronDownIcon
          strokeWidth={2}
          className={cn(
            "text-black min-w-[26px] cursor-pointer transition-transform duration-200 pointer-events-auto",
            !fantasyPointDetailsKeys.length && "opacity-0 pointer-events-none",
          )}
          onClick={toggleExpandedValue}
        />
      </AccordionTrigger>
      <AccordionContent className="pb-2">
        <div className="pl-[5rem]">
          {fantasyPointDetailsKeys.map((event: string) => {
            const stat = player.fantasyPointDetails[event as keyof FantasyPointDetails] ?? null;

            if (!stat || stat.occurrence === 0) {
              return null;
            }

            return (
              <div key={event} className="flex space-x-3 text-xs text-black w-full py-1">
                <span className="w-24 md:w-32">{PLAYER_STATS_DISPLAY_MAP[event]}</span>
                <span className="w-10 text-right">{stat.occurrence}回</span>
                <span className="w-10 text-primary text-right">{roundToFixed(stat.fantasyPoint)}pt</span>
              </div>
            );
          })}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
